import { useState, useEffect } from 'react';
import { Flex, Text, Link, Box } from '@chakra-ui/react';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

const scrollToSection = (sectionId: string) => {
  const section = document.getElementById(sectionId);
  if (section) {
    const position = section.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: position - 25,
      behavior: 'smooth',
    });
  }
};

interface ErrorLinkProps {
  label: string;
  errorMessage: string;
  sectionId: string;
}

const ErrorLink = ({ label, errorMessage, sectionId }: ErrorLinkProps) => (
  <li>
    <Link
      onClick={(e) => {
        e.preventDefault();
        scrollToSection(sectionId);
      }}
      textDecoration="underline"
      cursor="pointer"
      color="#FF1010"
      _hover={{ textDecoration: 'none' }}
    >
      [{label}]
    </Link>{' '}
    {errorMessage}
  </li>
);

interface PassErrorsBannerProps {
  errors: Record<string, string>;
}

const PassErrorsBanner = ({ errors }: PassErrorsBannerProps) => {
  const [displayedErrors, setDisplayedErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const errorCount = Object.keys(errors).length;
    if (errorCount === 0) {
      setDisplayedErrors({});
      return;
    }
    const timeoutId = setTimeout(() => {
      setDisplayedErrors(errors);
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [errors]);

  const errorCount = Object.keys(displayedErrors).length;
  if (errorCount === 0) return null;

  const renderErrorItem = (path: string) => {
    const message = displayedErrors[path];

    if (path === 'templateUuid') {
      return (
        <ErrorLink
          key={path}
          label="Pass template"
          errorMessage="is required"
          sectionId="pass-overview-section"
        />
      );
    }

    if (path.startsWith('barcode.')) {
      const field = path.split('.')[1];
      const labels: Record<string, string> = {
        type: 'Barcode type',
        source: 'Barcode source',
        value: 'Barcode value',
      };
      return (
        <ErrorLink
          key={path}
          label={labels[field] || 'Barcode'}
          errorMessage="is required"
          sectionId="nfc-and-barcode-section"
        />
      );
    }

    if (path === 'extId') {
      return (
        <ErrorLink
          key={path}
          label="External ID"
          errorMessage={
            message === 'External ID must be unique'
              ? 'already in use. Must be unique'
              : 'is required when used as barcode or NFC source'
          }
          sectionId="pass-overview-section"
        />
      );
    }

    if (path === 'nfcCustomValue') {
      return (
        <ErrorLink
          key={path}
          label="NFC value"
          errorMessage="is required"
          sectionId="nfc-and-barcode-section"
        />
      );
    }

    if (path.startsWith('localisation.')) {
      const parts = path.split('.');
      const fieldId = parts[1];
      const locale = parts[2];
      const fieldLabel = message.replace(
        / translation for .+ is required$/,
        ''
      );
      const isBackField =
        fieldId?.startsWith('back_') || fieldId?.startsWith('back');
      return (
        <ErrorLink
          key={path}
          label={`${fieldLabel} (${locale?.toUpperCase()})`}
          errorMessage="translation is required"
          sectionId={
            isBackField ? 'back-fields-section' : 'front-fields-section'
          }
        />
      );
    }

    if (path === 'expiryTime') {
      return (
        <ErrorLink
          key={path}
          label="Expiry time"
          errorMessage="is required when date is set"
          sectionId="pass-expiry-section"
        />
      );
    }

    if (path === 'featuredActions' || path.startsWith('featuredActions.')) {
      return (
        <ErrorLink
          key={path}
          label="Featured actions"
          errorMessage={message}
          sectionId="featured-actions-section"
        />
      );
    }

    if (path.startsWith('fieldValues.')) {
      const fieldId = path.replace('fieldValues.', '');
      const isBackField =
        fieldId.startsWith('back_') || fieldId.startsWith('back');
      const isUrlError = message.includes('https://');
      let fieldLabel: string;
      if (isUrlError) {
        fieldLabel = message.split(':')[0] || fieldId;
      } else {
        fieldLabel = message.replace(' is required', '');
      }
      return (
        <ErrorLink
          key={path}
          label={fieldLabel}
          errorMessage={
            isUrlError
              ? 'URL must start with https://, http://, mailto: or tel:'
              : 'is required'
          }
          sectionId={
            isBackField ? 'back-fields-section' : 'front-fields-section'
          }
        />
      );
    }

    return <li key={path}>{message}</li>;
  };

  return (
    <Box
      bg="#FFDBDB"
      w="full"
      mt={4}
      py={2}
      px={4}
      borderLeft="10px solid #FF1010"
    >
      <Flex direction="row" gap={3} alignItems="center">
        <CustomErrorIcon boxSize="20px" color="#FF1010" />
        <Text textStyle="bodyFamilySemiBold" fontSize="12px">
          {errorCount} error{errorCount > 1 ? 's' : ''} found
        </Text>
      </Flex>
      <Flex direction="column" pl={10}>
        <Text textStyle="bodyFamilyRegular" fontSize="12px">
          <ul>{Object.keys(displayedErrors).map(renderErrorItem)}</ul>
        </Text>
      </Flex>
    </Box>
  );
};

export default PassErrorsBanner;
